import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Music, Gift, Play, Pause, Share2, FileText, ChevronDown, ChevronUp, Video } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_gift-melody-2/artifacts/vgbmb5gp_Logo-Wrapped%20Music1.png";
const BANNER_STAFFS = "/assets/brand/logo-inline.png";

export default function GiftView() {
  const { giftId } = useParams();
  const navigate = useNavigate();
  const [giftData, setGiftData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showLyrics, setShowLyrics] = useState(false);
  const audioRef = useRef(null);

  // Handle play/pause toggle
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Sync isPlaying state with audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);
      const handleEnded = () => setIsPlaying(false);
      
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('ended', handleEnded);
      
      return () => {
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('pause', handlePause);
        audio.removeEventListener('ended', handleEnded);
      };
    }
  }, [giftData]);

  useEffect(() => {
    const fetchGift = async () => {
      try {
        const res = await fetch(`${API}/gifts/${giftId}`);
        if (res.ok) {
          const data = await res.json();
          setGiftData(data);
        }
      } catch (error) {
        console.error('Error fetching gift:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGift();
  }, [giftId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {[0, 0.1, 0.2, 0.3, 0.4].map((delay, i) => (
              <div key={i} className="audio-bar" style={{ animationDelay: `${delay}s` }}></div>
            ))}
          </div>
          <span className="text-white">Loading gift...</span>
        </div>
      </div>
    );
  }

  if (!giftData || !giftData.gift) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Gift className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Gift Not Found</h2>
          <p className="text-zinc-400 mb-6">This gift card may have been deleted or doesn't exist.</p>
          <Button onClick={() => navigate('/')} className="btn-primary">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const { gift, song, video, cover } = giftData;
  
  // Determine if this is a video or song gift
  const isVideoGift = !!video;
  const mediaTitle = isVideoGift ? (video?.title || 'Untitled Video') : (song?.title || 'Untitled Song');

  const coverImage = cover?.image_url 
    || (cover?.image_base64 ? `data:image/png;base64,${cover.image_base64}` : null)
    || (isVideoGift ? video?.thumbnail_url : song?.image_url);

  return (
    <div className="min-h-screen bg-background">
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-pink-500/10 via-background to-primary/10 pointer-events-none" />
      
      {/* Navigation */}
      <nav className="relative z-10 glass">
        <div className="container-wide flex items-center justify-center h-16">
          <a href="https://wrappedinmusic.com" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
            <img src={BANNER_STAFFS} alt="Wrapped In Music" className="h-10 w-auto" />
          </a>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-10 pt-12 pb-24 container-narrow">
        <div className="glass-card p-8 max-w-md mx-auto text-center">
          {/* Logo */}
          <div className="mb-6">
            <a href="https://wrappedinmusic.com" target="_blank" rel="noopener noreferrer" className="inline-block hover:opacity-80 transition-opacity">
              <img src={LOGO_URL} alt="Wrapped In Music" className="h-16 w-auto mx-auto" />
            </a>
          </div>

          {/* Recipient */}
          {gift.recipient_name && (
            <p className="text-zinc-400 mb-2">A gift for</p>
          )}
          {gift.recipient_name && (
            <h1 className="text-3xl font-bold text-white mb-6">{gift.recipient_name}</h1>
          )}

          {/* Cover/Song/Video Image */}
          <div className={`${isVideoGift ? 'aspect-video' : 'aspect-square'} rounded-xl overflow-hidden bg-black/30 mb-6 ${isVideoGift ? 'max-w-full' : 'max-w-[250px]'} mx-auto relative group`}>
            {isVideoGift && video?.video_url ? (
              <video 
                src={video.video_url}
                controls
                className="w-full h-full object-contain"
                poster={coverImage || video?.thumbnail_url}
              />
            ) : coverImage ? (
              <img 
                src={coverImage}
                alt="Cover"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {isVideoGift ? (
                  <Video className="h-16 w-16 text-white/20" />
                ) : (
                  <Music className="h-16 w-16 text-white/20" />
                )}
              </div>
            )}
            
            {/* Play overlay - only for songs */}
            {!isVideoGift && song?.audio_url && (
              <div 
                className={`absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity cursor-pointer ${isPlaying ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                onClick={togglePlay}
              >
                <button 
                  className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  {isPlaying ? (
                    <Pause className="h-8 w-8 text-white" />
                  ) : (
                    <Play className="h-8 w-8 text-white ml-1" />
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Song/Video Title */}
          <h2 className="text-2xl font-bold text-white mb-4">{mediaTitle}</h2>

          {/* Message */}
          <div className="bg-black/20 rounded-lg p-4 mb-6">
            <p className="text-zinc-300 italic">"{gift.message}"</p>
          </div>

          {/* Audio Player - only for songs */}
          {!isVideoGift && song?.audio_url && (
            <audio 
              ref={audioRef}
              src={song.audio_url} 
              controls 
              className="w-full mb-6"
              style={{ filter: 'invert(1)' }}
            />
          )}

          {/* Lyrics Section - only for songs */}
          {!isVideoGift && song?.lyrics && (
            <div className="mb-6">
              <button
                onClick={() => setShowLyrics(!showLyrics)}
                className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-white/5 hover:bg-white/10 rounded-lg text-zinc-300 transition-colors"
              >
                <FileText className="h-4 w-4" />
                <span>{showLyrics ? 'Hide Lyrics' : 'View Lyrics'}</span>
                {showLyrics ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              
              {showLyrics && (
                <div className="mt-4 bg-black/30 rounded-lg p-4 text-left">
                  <pre className="text-zinc-300 text-sm whitespace-pre-wrap font-sans leading-relaxed">
                    {song.lyrics}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* CTA */}
          <div className="pt-4 border-t border-white/10">
            <p className="text-zinc-400 text-sm mb-4">
              Want to create your own AI-generated songs?
            </p>
            <Button onClick={() => navigate('/')} className="btn-primary">
              Get Started Free
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-zinc-500 text-sm">
            Created with ❤️ on WrappedInMusic
          </p>
        </div>
      </main>
    </div>
  );
}
